import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { RoomResponse } from '../types/auth';
import {
  fetchUpcomingRoomsCached,
  filterRoomsByDirection,
  isRoomPast,
} from './useUpcomingRooms';

const STALE_MS = 30_000;

interface CacheEntry {
  data: RoomResponse[];
  fetchedAt: number;
}

export interface ArchiveRoomsFilters {
  direction?: string | null; // applied client-side via filterRoomsByDirection
  date?: string | null;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<RoomResponse[]>>();

function cacheKey(date: string | null | undefined): string {
  return date ?? '*';
}

/** Whether a room's start/end day matches the picked ISO date in local TZ. */
function isOnLocalDate(r: RoomResponse, isoDate: string): boolean {
  const t = r.endedAtUtc ?? r.startedAtUtc ?? r.scheduledAtUtc;
  if (!t) return false;
  const d = new Date(t);
  if (!Number.isFinite(d.getTime())) return false;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}` === isoDate;
}

/**
 * Module-level cached fetcher. Combines two sources so today's archive
 * isn't empty when BE hasn't formally flipped a room to Ended:
 *
 *   1. `api.fetchArchiveRooms({date})` — BE-classified archive (Ended +
 *      recording).
 *   2. `fetchUpcomingRoomsCached()` — public rooms list, then filtered
 *      client-side via `isPastRoom` (past + not live) so a practice
 *      that ran this morning surfaces on Архив even before BE catches
 *      up. Deduped against the BE list by id.
 *
 * Direction is applied client-side after the merge (mirrors UpcomingTab).
 */
export async function fetchArchiveRoomsCached(
  date: string | null | undefined,
): Promise<RoomResponse[]> {
  const key = cacheKey(date);
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.fetchedAt < STALE_MS) return hit.data;

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = Promise.all([
    api.fetchArchiveRooms({ date: date ?? undefined }),
    // Public-rooms fetch is best-effort — if it fails we still return BE
    // archive. The ?? [] also covers the cache-miss-on-error path.
    fetchUpcomingRoomsCached().catch(() => [] as RoomResponse[]),
  ])
    .then(([beArchive, allPublic]) => {
      const tNow = Date.now();
      let supplement = allPublic.filter((r) => isRoomPast(r, tNow));
      if (date) supplement = supplement.filter((r) => isOnLocalDate(r, date));
      const beIds = new Set(beArchive.map((r) => r.id));
      const data = [
        ...beArchive,
        ...supplement.filter((r) => !beIds.has(r.id)),
      ];
      cache.set(key, { data, fetchedAt: Date.now() });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, promise);
  return promise;
}

/** Test-only — clear the module cache between tests. */
export function _resetArchiveRoomsCacheForTest(): void {
  cache.clear();
  inflight.clear();
}

export function useArchiveRooms(filters: ArchiveRoomsFilters): {
  rooms: RoomResponse[];
  isLoading: boolean;
  error: unknown;
} {
  const direction = filters.direction ?? null;
  const date = filters.date ?? null;
  const key = cacheKey(date);

  const applyFilter = (data: RoomResponse[]): RoomResponse[] =>
    filterRoomsByDirection(data, direction);

  const [rooms, setRooms] = useState<RoomResponse[]>(() => {
    const hit = cache.get(key);
    return hit ? applyFilter(hit.data) : [];
  });
  const [isLoading, setIsLoading] = useState(!cache.get(key));
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let mounted = true;

    const hit = cache.get(key);
    if (hit && Date.now() - hit.fetchedAt < STALE_MS) {
      setRooms(applyFilter(hit.data));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchArchiveRoomsCached(date)
      .then((data) => {
        if (!mounted) return;
        setRooms(applyFilter(data));
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
    // applyFilter depends on direction; key already captures date.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, direction]);

  return { rooms, isLoading, error };
}
